import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Eye, EyeOff, ShieldCheck, User, Phone, Lock, Building2, Mail, ChevronRight, ArrowLeft, Delete } from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';
import { SITE } from '../utils/siteConfig';
import { getStaffHomeRoute } from '../utils/roleRouting';

const LoginPage = ({ defaultTab }) => {
  const tabMap = { admin: 'staff', staff: 'staff', client: 'client', entreprise: 'b2b', b2b: 'b2b' };
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [pinDigits, setPinDigits] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginMode, setLoginMode] = useState(tabMap[defaultTab] || 'staff');
  const [needs2FA, setNeeds2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendStatus, setResendStatus] = useState('');

  const { login, verify2FA, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, tError } = useLanguage();
  const canvasRef = useRef(null);

  const isClientLogin = loginMode === 'client';
  const isEmailFormat = username.includes('@') && loginMode === 'staff';
  const isGuyarepair = !SITE.isRemobiles;
  // Always show password field (no PIN pad) for all modes and all sites
  const showPasswordField = true;

  // Animated background
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    let particles = [];

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    for (let i = 0; i < 40; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        r: Math.random() * 2 + 1,
        o: Math.random() * 0.3 + 0.05,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${SITE.accentRgb}, ${p.o})`;
        ctx.fill();
      });
      // Connect nearby particles
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(${SITE.accentRgb}, ${0.06 * (1 - dist / 120)})`;
            ctx.stroke();
          }
        }
      }
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.role === 'super_admin' && !user.magasin_role) navigate('/super-admin');
      else if (user.role === 'client') navigate('/espace-client');
      else if (user.role === 'b2b') navigate('/espace-b2b');
      else navigate(getStaffHomeRoute(user));
    }
  }, [isAuthenticated, user, navigate]);

  const googleProcessed = useRef(false);

  // Phase 2 §2.1 — Google OAuth retiré. Le hash session_id est désormais ignoré
  // (l'endpoint backend renvoie 410 Gone). On nettoie simplement l'URL si on en
  // reçoit un, pour éviter qu'il reste collé après un ancien lien.
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=') && !googleProcessed.current) {
      googleProcessed.current = true;
      window.history.replaceState(null, '', window.location.pathname);
      setError(t('login.googleDisabled'));
    }
  }, []);

  const handleGoogleLogin = () => {
    // Phase 2 §2.1 — endpoint disabled. Kept for backward compat with any UI
    // button referencing it; surfaces a friendly error instead of redirecting.
    setError(t('login.googleDisabled'));
  };

  const handleResendVerification = async () => {
    setResendStatus('sending');
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await axios.post(`${API_URL}/api/auth/resend-verification`, { email: resendEmail });
      if (res.data.status === 'already_verified') {
        setEmailNotVerified(false);
        setResendStatus('');
        setError('');
      } else {
        setResendStatus('sent');
      }
    } catch (err) {
      setResendStatus('error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (loginMode === 'b2b') {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const res = await axios.post(`${API_URL}/api/b2b/login`, { username, password });
        localStorage.setItem('token', res.data.access_token);
        window.location.href = '/espace-b2b';
      } catch (err) {
        setError(tError(err.response?.data?.detail) || t('login.wrongCredentials'));
      }
      setLoading(false);
      return;
    }

    // All modes use the typed password field
    const result = await login(username, password, isClientLogin);
    if (result.success) {
      if (result.user.role === 'super_admin' && !result.user.magasin_role) navigate('/super-admin');
      else if (result.user.role === 'client') navigate('/espace-client');
      else navigate(getStaffHomeRoute(result.user));
    } else if (result.requires_2fa) {
      setNeeds2FA(true);
      setTempToken(result.temp_token);
    } else if (result.error === 'email_not_verified') {
      setEmailNotVerified(true);
      setResendEmail(username);
    } else { setError(tError(result.error)); }
    setLoading(false);
  };

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await verify2FA(tempToken, otpCode);
    if (result.success) {
      if (result.user.role === 'super_admin' && !result.user.magasin_role) navigate('/super-admin');
      else if (result.user.role === 'client') navigate('/espace-client');
      else navigate(getStaffHomeRoute(result.user));
    } else {
      setError(result.error || t('login.invalid2FA'));
    }
    setLoading(false);
  };

  const modeConfig = {
    staff: { accent: SITE.loginAccent, label: t('login.tabStaff'), sub: t('login.staffSpace') || 'Espace Personnel', icon: User },
    client: { accent: '#26c6da', label: t('login.tabClient'), sub: t('login.clientSpace') || 'Espace Client', icon: Phone },
    b2b: { accent: '#ffa726', label: t('login.tabBusiness'), sub: t('login.businessSpace') || 'Espace Entreprise', icon: Building2 },
  };
  const mode = modeConfig[loginMode];

  return (
    <div className="min-h-screen flex relative overflow-hidden" style={{ background: 'var(--rm-bg-base)' }}>
      {/* Animated canvas background */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" style={{ zIndex: 0, pointerEvents: 'none' }} />

      {/* Left decorative panel - hidden on mobile */}
      <div className="hidden lg:flex lg:w-[45%] relative items-center justify-center" style={{ zIndex: 1 }}>
        <div className="absolute inset-0" style={{ background: `linear-gradient(160deg, ${mode.accent}08 0%, transparent 50%)` }} />
        <div className="relative z-10 px-16 max-w-lg">
          {/* Brand mark */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: `${mode.accent}18`, border: `1px solid ${mode.accent}30` }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={mode.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-black tracking-tight text-white">{SITE.name}</h1>
                <p className="text-xs tracking-widest uppercase" style={{ color: `${mode.accent}90` }}>{SITE.subtitle}</p>
              </div>
            </div>
          </div>

          {/* Feature highlights */}
          <div className="space-y-6">
            {SITE.loginFeatures.map((f, i) => (
              <div key={i} className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-md flex items-center justify-center shrink-0 mt-0.5" style={{ background: `${mode.accent}10`, border: `1px solid ${mode.accent}20` }}>
                  <ChevronRight className="w-4 h-4" style={{ color: mode.accent }} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-white mb-1">{f.title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom line accent */}
          <div className="mt-12 flex items-center gap-3">
            <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, ${mode.accent}30, transparent)` }} />
            <span className="text-[10px] tracking-widest text-gray-600 uppercase">{t('login.frenchGuiana')}</span>
          </div>
        </div>
      </div>

      {/* Right login panel */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-8 relative" style={{ zIndex: 1 }}>
        {/* Language selector */}
        <div className="absolute top-5 right-5 z-30">
          <LanguageSelector />
        </div>

        <div className="w-full max-w-[400px]">
          {/* Mobile brand header */}
          <div className="lg:hidden text-center mb-8">
            <h1 className="text-2xl font-black tracking-tight text-white">{SITE.name}</h1>
            <p className="text-xs tracking-widest uppercase mt-1" style={{ color: `${mode.accent}90` }}>{mode.sub}</p>
          </div>

          {/* Mode title (desktop) */}
          <div className="hidden lg:block mb-8">
            <p className="text-xs tracking-widest uppercase mb-2" style={{ color: mode.accent }}>{mode.sub}</p>
            <h2 className="text-xl font-bold text-white">{t('login.connectYou') || 'Connectez-vous'}</h2>
          </div>

          {/* Mode tabs */}
          {!defaultTab && (
            <div className="flex mb-7 gap-2" data-testid="login-mode-tabs">
              {Object.entries(modeConfig).map(([key, cfg]) => {
                const Icon = cfg.icon;
                const active = loginMode === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => { setLoginMode(key); setError(''); setPinDigits(''); setPassword(''); }}
                    data-testid={`login-tab-${key}`}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-semibold transition-all duration-200"
                    style={{
                      background: active ? `${cfg.accent}15` : 'rgba(255,255,255,0.02)',
                      border: `1px solid ${active ? cfg.accent + '40' : 'rgba(255,255,255,0.06)'}`,
                      color: active ? cfg.accent : '#6b7280',
                    }}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {cfg.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* Error display */}
          {error && !emailNotVerified && (
            <div className="mb-5 px-4 py-3 rounded-lg text-xs text-center font-medium" data-testid="login-error"
              style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171' }}>
              {error}
            </div>
          )}

          {/* Email not verified banner */}
          {emailNotVerified && (
            <div className="mb-5 px-4 py-4 rounded-lg text-center" data-testid="email-not-verified-banner"
              style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)' }}>
              <Mail className="w-8 h-8 mx-auto mb-2" style={{ color: '#fbbf24' }} />
              <p className="text-sm font-semibold text-amber-400 mb-1">{t('login.emailNotVerifiedTitle')}</p>
              <p className="text-xs text-gray-400 mb-3">{t('login.emailNotVerifiedDesc')}</p>
              {resendStatus === 'sent' ? (
                <p className="text-xs text-green-400 font-semibold">{t('login.emailResent')}</p>
              ) : resendStatus === 'sending' ? (
                <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
                  <div className="w-3 h-3 border-2 border-gray-400/30 border-t-gray-400 rounded-full animate-spin" />
                  {t('login.sending')}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleResendVerification}
                  className="text-xs font-semibold px-4 py-2 rounded-lg transition-all"
                  style={{ background: 'rgba(251,191,36,0.15)', color: '#fbbf24' }}
                  data-testid="resend-verification-btn"
                >
                  {t('login.resendVerification')}
                </button>
              )}
              <button type="button" onClick={() => { setEmailNotVerified(false); setResendStatus(''); }}
                className="block mx-auto mt-2 text-[10px] text-gray-600 hover:text-gray-400 transition-colors">
                {t('login.back')}
              </button>
            </div>
          )}

          {needs2FA ? (
            <div data-testid="2fa-section">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${mode.accent}20` }}>
                  <ShieldCheck className="w-5 h-5" style={{ color: mode.accent }} />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white">{t('login.twoFATitle')}</h3>
                  <p className="text-xs text-gray-500">{t('login.twoFASubtitle')}</p>
                </div>
              </div>
              <form onSubmit={handle2FAVerify} className="space-y-4">
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                  data-testid="2fa-code-input"
                  className="w-full px-4 py-4 rounded-lg text-center text-2xl tracking-[0.5em] text-white font-mono placeholder-gray-600 focus:outline-none"
                  style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${mode.accent}40` }}
                  placeholder="000000"
                  autoComplete="one-time-code"
                  autoFocus
                  required
                />
                <button type="submit" disabled={loading || otpCode.length !== 6}
                  className="w-full py-3.5 rounded-lg font-semibold text-sm transition-all duration-200 disabled:opacity-40 flex items-center justify-center gap-2"
                  style={{ background: mode.accent, color: '#fff' }}
                  data-testid="2fa-verify-submit"
                >
                  {loading ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> {t('login.verifying')}</> : <><ShieldCheck className="w-4 h-4" /> {t('login.verify')}</>}
                </button>
                <button type="button" onClick={() => { setNeeds2FA(false); setTempToken(''); setOtpCode(''); setError(''); }}
                  className="w-full py-2 text-xs text-gray-500 hover:text-white transition-colors" data-testid="2fa-back-btn">
                  {t('login.back')}
                </button>
              </form>
            </div>
          ) : (
          /* Login form */
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Username / Phone / Email field */}
            <div>
              <label className="block text-[11px] uppercase tracking-wider mb-2 font-medium" style={{ color: '#6b7280' }}>
                {loginMode === 'client' ? t('login.phoneNumber') : loginMode === 'b2b' ? t('login.proEmail') : t('login.username')}
              </label>
              <div className="relative group">
                <input
                  type={loginMode === 'b2b' ? 'email' : 'text'}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  data-testid="login-username"
                  className="w-full pl-11 pr-4 py-3.5 rounded-lg text-sm text-white placeholder-gray-600 transition-all duration-200 focus:outline-none"
                  style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: `1px solid rgba(255,255,255,0.08)`,
                  }}
                  onFocus={(e) => { e.target.style.borderColor = `${mode.accent}40`; e.target.style.boxShadow = `0 0 0 3px ${mode.accent}08`; }}
                  onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.08)'; e.target.style.boxShadow = 'none'; }}
                  placeholder={loginMode === 'client' ? '0694 12 34 56' : loginMode === 'b2b' ? 'contact@entreprise.com' : t('login.yourId')}
                  required
                  autoFocus
                />
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: '#4b5563' }}>
                  {loginMode === 'b2b' ? <Mail className="w-4 h-4" /> : loginMode === 'client' ? <Phone className="w-4 h-4" /> : <User className="w-4 h-4" />}
                </div>
              </div>
            </div>

            {/* Password field for b2b/email login or guyarepair staff */}
            {showPasswordField ? (
              <div>
                <label className="block text-[11px] uppercase tracking-wider mb-2 font-medium" style={{ color: '#6b7280' }}>
                  {loginMode === 'b2b' ? t('login.b2bPassword') : t('login.password')}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password"
                    className="w-full pl-11 pr-12 py-3.5 rounded-lg text-sm text-white placeholder-gray-600 transition-all duration-200 focus:outline-none"
                    style={{
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.08)',
                    }}
                    onFocus={(e) => { e.target.style.borderColor = `${mode.accent}40`; e.target.style.boxShadow = `0 0 0 3px ${mode.accent}08`; }}
                    onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.08)'; e.target.style.boxShadow = 'none'; }}
                    placeholder={loginMode === 'b2b' ? t('login.b2bPlaceholder') : t('login.passwordPlaceholder')}
                    required
                  />
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#4b5563' }} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 transition-colors" style={{ color: '#6b7280' }}>
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            ) : (
              /* PIN keypad */
              <div>
                <label className="block text-[11px] uppercase tracking-wider mb-2 font-medium" style={{ color: '#6b7280' }}>
                  {t('login.pin')}
                </label>
                {/* PIN display dots */}
                <div className="flex items-center justify-center gap-3 mb-4 py-3">
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      className="w-3 h-3 rounded-full transition-all duration-300"
                      style={{
                        background: i < pinDigits.length ? mode.accent : 'rgba(255,255,255,0.06)',
                        boxShadow: i < pinDigits.length ? `0 0 8px ${mode.accent}40` : 'none',
                        transform: i < pinDigits.length ? 'scale(1.2)' : 'scale(1)',
                      }}
                    />
                  ))}
                </div>
                <input
                  type="hidden"
                  value={pinDigits}
                  data-testid="pin-input"
                />
                {/* Numeric keypad */}
                <div className="grid grid-cols-3 gap-2 max-w-[260px] mx-auto">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => { setError(''); if (pinDigits.length < 6) setPinDigits(pinDigits + String(n)); }}
                      className="py-3 rounded-lg text-base font-semibold text-white transition-all duration-150 active:scale-95"
                      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
                      onMouseEnter={(e) => e.target.style.background = `${mode.accent}10`}
                      onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.03)'}
                    >
                      {n}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setPinDigits(pinDigits.slice(0, -1))}
                    className="py-3 rounded-lg text-sm text-gray-500 transition-all duration-150 flex items-center justify-center"
                    style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}
                  >
                    <Delete className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => { setError(''); if (pinDigits.length < 6) setPinDigits(pinDigits + '0'); }}
                    className="py-3 rounded-lg text-base font-semibold text-white transition-all duration-150 active:scale-95"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
                    onMouseEnter={(e) => e.target.style.background = `${mode.accent}10`}
                    onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.03)'}
                  >
                    0
                  </button>
                  <button
                    type="button"
                    onClick={() => setPinDigits('')}
                    className="py-3 rounded-lg text-xs font-medium text-gray-500 transition-all duration-150"
                    style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}
                  >
                    C
                  </button>
                </div>
                <p className="text-[10px] text-gray-600 mt-3 text-center">{t('login.pinHint')}</p>
              </div>
            )}

            <input type="hidden" value={password} readOnly />

            {/* Submit button */}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full py-3.5 rounded-lg font-semibold text-sm transition-all duration-200 disabled:opacity-40 active:scale-[0.98] flex items-center justify-center gap-2"
              style={{
                background: mode.accent,
                color: '#080c14',
                boxShadow: `0 4px 20px ${mode.accent}30`,
              }}
              onMouseEnter={(e) => { if (!loading) e.target.style.boxShadow = `0 6px 28px ${mode.accent}50`; }}
              onMouseLeave={(e) => { e.target.style.boxShadow = `0 4px 20px ${mode.accent}30`; }}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                  {t('login.connecting')}
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4" />
                  {t('login.connect')}
                </>
              )}
            </button>
          </form>
          )}

          {/* Phase 2 §2.1 — Bouton "Se connecter avec Google" retiré (Google OAuth désactivé). */}

          {/* Links */}
          {loginMode === 'client' && (
            <div className="mt-6 text-center space-y-2">
              <p className="text-gray-500 text-xs">
                {t('login.noAccount')}{' '}
                <Link to="/inscription-client" className="font-semibold transition-colors" style={{ color: mode.accent }}>{t('login.createAccount')}</Link>
              </p>
              <Link to="/mot-de-passe-oublie" className="text-gray-600 text-[10px] transition-colors hover:text-gray-400" data-testid="client-forgot-password">{t('login.forgotPwd')}</Link>
            </div>
          )}

          {loginMode === 'b2b' && (
            <div className="mt-6 text-center space-y-2">
              <p className="text-gray-500 text-xs">
                {t('login.notRegistered')}{' '}
                <Link to="/inscription-b2b" className="font-semibold transition-colors" style={{ color: mode.accent }}>{t('login.businessRegister')}</Link>
              </p>
              <Link to="/mot-de-passe-oublie" className="text-gray-600 text-[10px] transition-colors hover:text-gray-400" data-testid="b2b-forgot-password">{t('login.forgotPwd')}</Link>
            </div>
          )}

          {/* Footer */}
          <div className="mt-8 flex items-center justify-between text-[10px] text-gray-600">
            <div className="flex items-center gap-1.5">
              <ShieldCheck className="w-3 h-3" style={{ color: mode.accent }} />
              <span>{t('login.secure')}</span>
            </div>
            <Link to="/" className="flex items-center gap-1 transition-colors hover:text-gray-400" data-testid="back-to-home">
              <ArrowLeft className="w-3 h-3" />
              {t('login.backHome')}
            </Link>
          </div>

          <div className="mt-4 text-center">
            <p className="text-[10px] text-gray-700">&copy; {new Date().getFullYear()} {SITE.name}. {t('login.allRightsReserved')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
